import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../../components/base/PageMeta';
import JsonLd from '../../components/base/JsonLd';
import FloatingCtaBar from '../../components/feature/FloatingCtaBar';
import SignUpModal from '../../components/feature/SignUpModal';
import SiteFooter from '../../components/feature/SiteFooter';

const allTools = [
  {
    slug: 'magai',
    name: 'Magai',
    category: 'AI Tools',
    categoryColor: 'text-teal-600',
    rating: '8.9',
    image: 'https://public.readdy.ai/ai/img_res/975de2405551e25a133b7fcb7f371f3f.jpg',
    desc: 'All-in-one AI platform that combines multiple AI models including GPT-4, Claude, and Gemini with a clean, distraction-free interface.',
    features: ['Access to 20+ AI models', 'Custom persona creation', 'Team workspace & collaboration'],
  },
  {
    slug: 'invideo',
    name: 'InVideo',
    category: 'AI Tools',
    categoryColor: 'text-teal-600',
    rating: '10',
    image: 'https://readdy.ai/api/search-image?query=InVideo%20AI-powered%20online%20video%20creation%20platform%20dashboard%20dark%20theme%20with%20colorful%20video%20timeline%20editor%20media%20library%20panel%20and%20thumbnail%20previews%2C%20professional%20video%20software%20screenshot%20with%20vibrant%20gradient%20clips&width=400&height=220&seq=rv_invideo01&orientation=landscape',
    desc: 'AI-powered video creation platform that transforms scripts and text into professional video content with voiceovers automatically.',
    features: ['Text-to-video in minutes', 'AI voiceover & avatar', '5000+ customizable templates'],
  },
  {
    slug: 'elevenlabs',
    name: 'ElevenLabs',
    category: 'AI Tools',
    categoryColor: 'text-teal-600',
    rating: '7.1',
    image: 'https://readdy.ai/api/search-image?query=ElevenLabs%20AI%20voice%20synthesis%20platform%20interface%20showing%20audio%20waveform%20visualization%20with%20teal%20purple%20colors%20and%20voice%20model%20library%20panel%2C%20futuristic%20sound%20wave%20UI%2C%20professional%20audio%20tech%20software%20screenshot%20dark%20background&width=400&height=220&seq=rv_eleven01&orientation=landscape',
    desc: 'Leading AI voice synthesis platform offering incredibly realistic text-to-speech and custom voice cloning technology.',
    features: ['Ultra-realistic AI voices', 'Voice cloning from 1 min audio', 'Supports 29 languages'],
  },
  {
    slug: 'skylum',
    name: 'Skylum',
    category: 'Design Tools',
    categoryColor: 'text-orange-600',
    rating: '5.9',
    image: 'https://readdy.ai/api/search-image?query=Skylum%20Luminar%20Neo%20AI%20photo%20editing%20software%20showing%20dramatic%20landscape%20nature%20photo%20sky%20replacement%20with%20golden%20sunset%2C%20dark%20modern%20editing%20UI%20with%20tools%20sidebar%20and%20adjustment%20sliders%2C%20professional%20photography%20software%20screenshot&width=400&height=220&seq=rv_skylum01&orientation=landscape',
    desc: 'AI-powered photo editing software for Mac and Windows featuring Luminar Neo with sky replacement and AI portrait enhancements.',
    features: ['AI Sky Replacement', 'GenErase & GenExpand tools', 'One-click AI photo enhance'],
  },
  {
    slug: 'akool',
    name: 'Akool',
    category: 'AI Tools',
    categoryColor: 'text-teal-600',
    rating: '9.1',
    image: 'https://readdy.ai/api/search-image?query=Akool%20generative%20AI%20platform%20for%20personalized%20marketing%20content%20creation%20showing%20face%20swap%20video%20ad%20generation%20interface%2C%20modern%20clean%20dashboard%20UI%20with%20campaign%20management%20and%20colorful%20content%20preview%20cards%20on%20light%20background&width=400&height=220&seq=rv_akool01&orientation=landscape',
    desc: 'Generative AI platform for creating personalized marketing content with face swap, talking avatar, and video translation capabilities.',
    features: ['Real-time face swap tech', 'Talking avatar video ads', 'Video translation in 30+ languages'],
  },
  {
    slug: 'webflow',
    name: 'Webflow',
    category: 'Website Builders',
    categoryColor: 'text-indigo-600',
    rating: '9.2',
    image: 'https://readdy.ai/api/search-image?query=Webflow%20professional%20website%20builder%20visual%20design%20interface%20showing%20canvas%20with%20responsive%20web%20page%20layout%20element%20panel%20and%20CMS%20collections%2C%20clean%20modern%20UI%20with%20dark%20sidebar%20and%20white%20canvas%2C%20professional%20no-code%20web%20design%20screenshot&width=400&height=220&seq=rv_webflow01&orientation=landscape',
    desc: 'Professional no-code website builder with advanced design capabilities, powerful CMS and built-in hosting for designers.',
    features: ['Visual CSS design tools', 'Powerful CMS & ecommerce', 'Built-in hosting & CDN'],
  },
  {
    slug: 'chatgpt-plus',
    name: 'ChatGPT Plus',
    category: 'AI Tools',
    categoryColor: 'text-teal-600',
    rating: '9.4',
    image: 'https://readdy.ai/api/search-image?query=ChatGPT%20Plus%20interface%20showing%20advanced%20AI%20conversation%20with%20code%20generation%20and%20image%20creation%20tools%2C%20clean%20minimal%20dark%20chat%20UI%20with%20GPT-4%20model%20selector%2C%20code%20block%20output%20and%20data%20analysis%20charts%2C%20professional%20AI%20assistant%20screenshot&width=400&height=220&seq=rv_chatgpt01&orientation=landscape',
    desc: 'Advanced AI assistant powered by GPT-4 with code generation, image creation via DALL-E, and internet browsing capabilities.',
    features: ['GPT-4o with vision', 'DALL-E 3 image generation', 'Custom GPTs & plugins'],
  },
  {
    slug: 'mailchimp',
    name: 'Mailchimp',
    category: 'Marketing Tools',
    categoryColor: 'text-orange-600',
    rating: '8.1',
    image: 'https://readdy.ai/api/search-image?query=Mailchimp%20email%20marketing%20platform%20dashboard%20showing%20campaign%20performance%20analytics%20open%20rate%20click%20rate%20graphs%20audience%20overview%20charts%2C%20clean%20professional%20UI%20with%20yellow%20and%20white%20brand%20colors%2C%20email%20marketing%20software%20screenshot&width=400&height=220&seq=rv_mailchimp01&orientation=landscape',
    desc: 'All-in-one email marketing platform with automation, landing pages, and audience management for businesses of all sizes.',
    features: ['Drag-and-drop email builder', 'Advanced audience segmentation', 'A/B testing & automation'],
  },
  {
    slug: 'figma',
    name: 'Figma',
    category: 'Design Tools',
    categoryColor: 'text-orange-600',
    rating: '9.6',
    image: 'https://readdy.ai/api/search-image?query=Figma%20UI%20design%20platform%20dashboard%20showing%20collaborative%20design%20canvas%20with%20component%20library%20frames%20and%20prototype%20flows%2C%20colorful%20modern%20interface%20design%20tool%20with%20layers%20panel%20and%20design%20properties%20sidebar%2C%20professional%20web%20app%20design%20screenshot&width=400&height=220&seq=rv_figma01&orientation=landscape',
    desc: 'Collaborative interface design tool with real-time co-editing, prototyping, and design system management for design teams.',
    features: ['Real-time multiplayer editing', 'Component libraries & tokens', 'Interactive prototyping'],
  },
  {
    slug: 'notion',
    name: 'Notion',
    category: 'Productivity',
    categoryColor: 'text-purple-600',
    rating: '8.7',
    image: 'https://readdy.ai/api/search-image?query=Notion%20all-in-one%20workspace%20platform%20showing%20docs%20database%20wiki%20kanban%20board%20and%20project%20management%20views%20in%20clean%20minimal%20white%20UI%2C%20productivity%20app%20screenshot%20with%20sidebar%20navigation%20and%20page%20content%20editor&width=400&height=220&seq=rv_notion01&orientation=landscape',
    desc: 'All-in-one workspace for notes, docs, wikis, and project management with powerful database and AI writing assistance.',
    features: ['Flexible blocks & databases', 'Notion AI writing assistant', 'Team wikis & docs'],
  },
  {
    slug: 'canva-pro',
    name: 'Canva Pro',
    category: 'Design Tools',
    categoryColor: 'text-orange-600',
    rating: '9.0',
    image: 'https://readdy.ai/api/search-image?query=Canva%20Pro%20graphic%20design%20platform%20showing%20drag%20and%20drop%20editor%20with%20social%20media%20template%20library%20brand%20kit%20and%20design%20elements%20panel%2C%20clean%20colorful%20modern%20UI%20with%20white%20canvas%20and%20side%20template%20panel%2C%20professional%20graphic%20design%20screenshot&width=400&height=220&seq=rv_canva01&orientation=landscape',
    desc: 'Powerful yet easy graphic design platform with 610,000+ templates, AI tools, and brand kit for social media and marketing.',
    features: ['610K+ professional templates', 'AI Magic Design & Resize', 'Brand Kit & team sharing'],
  },
  {
    slug: 'ahrefs',
    name: 'Ahrefs',
    category: 'SEO Tools',
    categoryColor: 'text-green-700',
    rating: '9.3',
    image: 'https://readdy.ai/api/search-image?query=Ahrefs%20SEO%20tool%20dashboard%20showing%20domain%20rating%20backlink%20profile%20organic%20traffic%20keyword%20rankings%20charts%20and%20site%20audit%20interface%2C%20clean%20professional%20SEO%20analytics%20software%20screenshot%20with%20orange%20and%20white%20colors&width=400&height=220&seq=rv_ahrefs01&orientation=landscape',
    desc: 'Comprehensive SEO toolkit for keyword research, backlink analysis, content research, rank tracking and competitive analysis.',
    features: ['170+ billion keyword database', 'Site audit & health score', 'Competitor content analysis'],
  },
  {
    slug: 'slack',
    name: 'Slack',
    category: 'Communication',
    categoryColor: 'text-pink-600',
    rating: '8.5',
    image: 'https://readdy.ai/api/search-image?query=Slack%20team%20communication%20platform%20interface%20showing%20channel%20sidebar%20with%20message%20threads%20emoji%20reactions%20file%20sharing%20and%20app%20integrations%2C%20clean%20modern%20team%20chat%20UI%20with%20purple%20accent%20colors%2C%20professional%20workplace%20messaging%20screenshot&width=400&height=220&seq=rv_slack01&orientation=landscape',
    desc: 'Team communication platform with channels, threads, and powerful integrations for remote and hybrid work teams.',
    features: ['Organized channels & threads', '2,600+ app integrations', 'Slack AI summarize & search'],
  },
  {
    slug: 'grammarly-premium',
    name: 'Grammarly Premium',
    category: 'Writing Tools',
    categoryColor: 'text-cyan-600',
    rating: '8.8',
    image: 'https://readdy.ai/api/search-image?query=Grammarly%20Premium%20AI%20writing%20assistant%20browser%20extension%20showing%20grammar%20spelling%20clarity%20suggestions%20in%20Google%20Docs%20with%20green%20score%20sidebar%2C%20clean%20writing%20improvement%20UI%20with%20teal%20green%20accent%20colors%2C%20professional%20writing%20tool%20screenshot%20on%20white%20background&width=400&height=220&seq=rv_gramm01&orientation=landscape',
    desc: 'AI-powered writing assistant that checks grammar, clarity, tone, and style across every platform you write on.',
    features: ['Real-time grammar & spelling', 'Tone detector & rewriting', 'Plagiarism checker'],
  },
  {
    slug: 'trello',
    name: 'Trello',
    category: 'Project Management',
    categoryColor: 'text-sky-600',
    rating: '8.2',
    image: 'https://readdy.ai/api/search-image?query=Trello%20project%20management%20kanban%20board%20interface%20showing%20colorful%20cards%20lists%20workflows%20and%20team%20member%20avatars%2C%20clean%20modern%20task%20management%20UI%20with%20blue%20and%20white%20colors%2C%20professional%20project%20tracking%20software%20screenshot&width=400&height=220&seq=rv_trello01&orientation=landscape',
    desc: 'Visual project management tool using Kanban boards, lists and cards to organize your team\'s tasks and workflows.',
    features: ['Intuitive drag-and-drop boards', 'Automation with Butler', 'Timeline & calendar views'],
  },
  {
    slug: 'asana',
    name: 'Asana',
    category: 'Project Management',
    categoryColor: 'text-sky-600',
    rating: '8.9',
    image: 'https://readdy.ai/api/search-image?query=Asana%20project%20management%20platform%20dashboard%20showing%20team%20workload%20task%20assignments%20timeline%20Gantt%20chart%20and%20goals%20tracking%2C%20clean%20modern%20project%20management%20UI%20with%20coral%20pink%20accent%20colors%20and%20white%20background&width=400&height=220&seq=rv_asana01&orientation=landscape',
    desc: 'Work management platform for teams to organize projects, track progress, and manage workloads across all departments.',
    features: ['Goals & portfolio tracking', 'Workflow automation rules', 'Workload balancing view'],
  },
  {
    slug: 'hubspot',
    name: 'HubSpot',
    category: 'Marketing Tools',
    categoryColor: 'text-orange-600',
    rating: '9.1',
    image: 'https://readdy.ai/api/search-image?query=HubSpot%20CRM%20and%20marketing%20platform%20dashboard%20showing%20sales%20pipeline%20contacts%20email%20campaigns%20analytics%20and%20deal%20tracking%20charts%2C%20professional%20marketing%20software%20UI%20with%20orange%20and%20white%20colors%2C%20all-in-one%20business%20software%20screenshot&width=400&height=220&seq=rv_hubspot01&orientation=landscape',
    desc: 'Complete CRM platform with marketing, sales, and service tools to help businesses attract, engage, and delight customers.',
    features: ['Free CRM with unlimited users', 'Email marketing & automation', 'Sales pipeline & forecasting'],
  },
  {
    slug: 'semrush',
    name: 'Semrush',
    category: 'SEO Tools',
    categoryColor: 'text-green-700',
    rating: '9.2',
    image: 'https://readdy.ai/api/search-image?query=Semrush%20SEO%20and%20digital%20marketing%20platform%20showing%20keyword%20research%20position%20tracking%20traffic%20analytics%20and%20competitive%20intelligence%20dashboard%2C%20clean%20professional%20marketing%20tool%20screenshot%20with%20orange%20and%20white%20brand%20colors&width=400&height=220&seq=rv_semrush01&orientation=landscape',
    desc: 'All-in-one digital marketing platform for SEO, PPC, content, social media, and competitive research with 55+ tools.',
    features: ['55+ marketing tools', 'Traffic & keyword analytics', 'Content marketing platform'],
  },
];

const categories = [
  'All', 'Website Builders', 'AI Tools', 'Marketing Tools', 'Design Tools',
  'Productivity', 'SEO Tools', 'Communication', 'Writing Tools', 'Project Management',
];

const categoryMap: Record<string, string> = {
  'Website Builders': 'Website Builders',
  'AI Tools': 'AI Tools',
  'Marketing Tools': 'Marketing Tools',
  'Design Tools': 'Design Tools',
  'Productivity': 'Productivity',
  'SEO Tools': 'SEO Tools',
  'Communication': 'Communication',
  'Writing Tools': 'Writing Tools',
  'Project Management': 'Project Management',
};

export default function ReviewsPage() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('All');
  const [showSignUp, setShowSignUp] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Tool Reviews – AI, SaaS, Design & More | 247digitrend',
    url: `${siteUrl}/reviews`,
    description:
      'In-depth, unbiased reviews of the most popular digital tools. Detailed analysis of AI assistants, website builders, marketing platforms, SEO tools and more.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Reviews', item: `${siteUrl}/reviews` },
      ],
    },
    mainEntity: {
      '@type': 'ItemList',
      name: 'Tool Reviews',
      numberOfItems: allTools.length,
      itemListElement: allTools.map((tool, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Review',
          name: `${tool.name} Review`,
          url: `${siteUrl}/reviews/${tool.slug}`,
          reviewBody: tool.desc,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: tool.rating,
            bestRating: '10',
          },
          itemReviewed: {
            '@type': 'SoftwareApplication',
            name: tool.name,
            applicationCategory: tool.category,
          },
        },
      })),
    },
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = activeTab === 'All'
    ? allTools
    : allTools.filter(t => t.category === categoryMap[activeTab]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="Tool Reviews – Unbiased AI, SaaS & Software Analysis | 247digitrend"
        description="Read unbiased, expert reviews of 18+ digital tools including ChatGPT, Figma, Notion, Ahrefs, HubSpot and more. Transparent scoring, pros, cons and pricing breakdowns."
        canonical={`${siteUrl}/reviews`}
        keywords="software tool reviews, AI tools review, SaaS review, Figma review, Notion review, compare247 reviews"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── SIGN UP MODAL ── */}
      <SignUpModal isOpen={showSignUp} onClose={() => setShowSignUp(false)} />

      {/* ── HEADER ── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247compare" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[19px] font-semibold text-violet-600 cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Blog</a>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="pt-44 pb-10" style={{ background: 'linear-gradient(160deg, #faf5ff 0%, #fdf2ff 50%, #fff0fb 100%)' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-black text-gray-900 mb-3 tracking-tight">
            Tool <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-fuchsia-500">Reviews</span>
          </h1>
          <p className="text-base text-gray-500 leading-relaxed max-w-xl mx-auto">
            In-depth reviews and analysis of the most popular tools in the market. Find the perfect solution for your business needs.
          </p>
        </div>
      </section>

      {/* ── CATEGORY TABS ── */}
      <section className="sticky top-[60px] z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveTab(cat)}
                className={`whitespace-nowrap text-xs font-semibold px-4 py-2 rounded-full transition-all cursor-pointer ${
                  activeTab === cat
                    ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white'
                    : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── CARDS GRID ── */}
      <section className="py-12" style={{ background: '#faf5ff' }}>
        <div className="max-w-6xl mx-auto px-8">
          {filtered.length === 0 ? (
            <div className="text-center py-24 text-gray-400 text-sm">No tools found in this category.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {filtered.map((tool, i) => (
                <a
                  key={i}
                  href={`/reviews/${tool.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-violet-200 hover:shadow-lg transition-all block cursor-pointer"
                >
                  {/* Image */}
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={tool.image}
                      alt={tool.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Rating badge */}
                    <div className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-white/95 rounded-full px-2.5 py-1 shadow-sm">
                      <i className="ri-star-fill text-yellow-400 text-xs"></i>
                      <span className="text-xs font-black text-gray-800">{tool.rating}</span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-4">
                    {/* Name + category */}
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base font-black text-gray-900">{tool.name}</h3>
                      <span className={`text-xs font-semibold ${tool.categoryColor} whitespace-nowrap`}>
                        {tool.category}
                      </span>
                    </div>

                    {/* Description */}
                    <p className="text-xs text-gray-500 leading-relaxed mb-3 line-clamp-2">{tool.desc}</p>

                    {/* Key features */}
                    <div className="mb-3">
                      <p className="text-xs font-bold text-gray-700 mb-1.5">Key features:</p>
                      <ul className="space-y-1">
                        {tool.features.map((f, fi) => (
                          <li key={fi} className="flex items-start gap-1.5">
                            <i className="ri-checkbox-circle-fill text-teal-500 text-xs mt-0.5 flex-shrink-0"></i>
                            <span className="text-xs text-gray-600 leading-tight">{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Read full review */}
                    <div className="pt-2.5 border-t border-gray-100">
                      <span className="text-xs font-semibold text-teal-600 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                        Read Full Review <i className="ri-arrow-right-up-line text-xs"></i>
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}

          {/* Load more */}
          <div className="text-center mt-10">
            <button className="inline-flex items-center gap-2 border-2 border-violet-600 text-violet-600 text-sm font-semibold px-8 py-3 rounded-full cursor-pointer hover:bg-violet-50 transition-colors whitespace-nowrap">
              <i className="ri-refresh-line"></i>
              Load More Reviews
            </button>
          </div>
        </div>
      </section>

      {/* ── NEWSLETTER ── */}
      <section className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 25%, #c026d3 60%, #ec4899 100%)' }}>
        <div className="max-w-2xl mx-auto px-6 flex items-center justify-center">
          <div className="w-full rounded-2xl border border-white/20 bg-white/10 backdrop-blur-md px-10 py-12 text-center text-white" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-6 rounded-xl border border-white/30 bg-white/10">
              <i className="ri-mail-line text-2xl text-white"></i>
            </div>
            <h3 className="text-2xl font-black mb-3 text-white">Stay Updated with Latest Reviews</h3>
            <p className="text-sm text-white/80 leading-relaxed mb-7">
              Get weekly insights, tool comparisons, and exclusive tips delivered straight to your inbox. Join 10,000+ subscribers.
            </p>
            <div className="flex items-center gap-2 max-w-md mx-auto mb-4">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-5 py-3 rounded-full text-sm text-gray-800 placeholder-gray-400 bg-white/90 outline-none border-none"
              />
              <button className="bg-white text-violet-700 font-bold text-sm px-6 py-3 rounded-full whitespace-nowrap cursor-pointer hover:bg-violet-50 transition-colors">
                Subscribe Now
              </button>
            </div>
            <p className="text-xs text-white/60">No spam, unsubscribe anytime. We respect your privacy.</p>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <SiteFooter />

      <FloatingCtaBar onSignUp={() => setShowSignUp(true)} />
    </div>
  );
}
